// The quantity / unit / price a review line is sent to A+ with. Shared by submit (App.jsx) and the
// line card (LineItems.jsx), so the CSR sees exactly what A+ will get.
//
// A line in the item's stocking unit 2 that converts to a whole number of stocking unit 1 goes in
// unit 1, the way CSRs key it: Sunshine Bouquet 6210460 (stocked SK, 36 CS/SK) ordered 288 Case
// at $27.544/CS, which goes in as 8 SK @ $991.584 (A+ prices it per CS, so it shows $27.544/CS).
// It went to A+ as 288 SK before (AI1H6, AI1JN, AI1SQ, AI1ZG in prod, AI0I1 in TE).

const round5 = n => Math.round(n * 100000) / 100000;

function sameUom(a, b, normalize) {
  return !!a && !!b && normalize(a) === normalize(b);
}

// The unit an unresolved line's PO quantity is in, as an A+ stocking unit: the PO's own unit when
// it is one of the item's stocking units, otherwise stocking unit 1 (IMUNM1).
export function orderedUom(item, normalize) {
  const match = [item.unitOfMeasure, item.stockingUom2, item.stockingUom3]
    .find(su => su && su.trim() && sameUom(su, item.poUnitOfMeasure, normalize));
  return match ? match.trim() : item.unitOfMeasure;
}

// The customer's own ordered unit and quantity, when the line goes to A+ in a different unit.
// Sent as CustomerUnitOfMeasure / CustomerOrderQuantity (A+ XDCOUM / XDCQTO) so the order shows
// both units, the way manual entry does with a customer default U/M: Durand 6010398 ordered
// 864 RL, goes in as 216 CS + 864 RL. Taken from a resolution that read the PO unit as an alt UM
// (customerUom), else the backend's automatic conversion of the PO unit (poUomConverted).
// Only alt UMs (IMAUM) go over: A+ converts the customer quantity through IMAUM alone, so a
// stocking unit is overwritten with the stock quantity. Sunshine 6210460 288 CS -> 8 SK came in
// as 8 SK + 8 CS (warning 1770; AI0IB, AI0IJ, AI0IL, AI0IO). Trey: keep that check in OP.
function customerUnit(item, res, normalize) {
  if (res?.customerUom && Number(res.customerQuantity) > 0) {
    return { uom: res.customerUom, quantity: Number(res.customerQuantity) };
  }
  // The backend converted the PO's unit to the A+ unit (864 RL -> 216 CS). Only a unit set up on
  // the item in A+ goes over as the customer's; a fixed one alone (4,000 EA -> 4 M) doesn't.
  const conv = item.poUomConverted;
  if (conv?.is_item_unit && Number(conv.quantity) > 0) {
    // item_unit_code is A+'s own code (PC); normalizing would send PC as EA.
    return { uom: conv.item_unit_code || normalize(conv.uom), quantity: Number(conv.quantity) };
  }
  return null;
}

export function aplusOrderLine(item, normalize) {
  const res = item.resolution;
  const line = res
    ? { quantity: res.quantity, uom: res.uom, unitPrice: res.unitPrice }
    : { quantity: item.poQuantity, uom: orderedUom(item, normalize), unitPrice: item.extUnitPrice };

  let result = { ...line, convertedFrom: null };
  const uom1 = (item.unitOfMeasure || '').trim();
  const factor = Number(item.uomConversion1);  // IMUMC1: stocking unit 2 per stocking unit 1
  const qty = Number(line.quantity);
  if (uom1 && factor > 0 && qty > 0
      && sameUom(line.uom, item.stockingUom2, normalize) && !sameUom(line.uom, uom1, normalize)) {
    const stockQty = round5(qty / factor);
    if (Math.abs(stockQty - Math.round(stockQty)) < 1e-6) {
      result = {
        quantity: Math.round(stockQty),
        uom: uom1,
        unitPrice: line.unitPrice != null ? round5(Number(line.unitPrice) * factor) : line.unitPrice,
        convertedFrom: { quantity: qty, uom: (line.uom || '').trim(), factor },
      };
    }
  }
  const customer = customerUnit(item, res, normalize);
  return { ...result, customer: customer && !sameUom(customer.uom, result.uom, normalize) ? customer : null };
}
